import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: Privacy
});

function Privacy() {
  return (
    <div className="from-background-gradient via-background to-background text-tx-primary scroll-primary flex h-screen w-screen flex-col overflow-auto bg-gradient-to-b p-8 antialiased">
      <h1 className="text-tx-primary mb-4 text-3xl font-bold">Privacy Policy</h1>
      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Last updated: September 20, 2023</h2>
      <p className="mb-6">
        This Privacy Policy applies to all products, services, and websites built and maintained by Moe Capital LLC,
        including anime.gf and anime.bf. This policy governs our handling of information about site visitors,
        prospective customers, and customers, as well as authorized users in relation to their procurement of our
        services and management of their relationship with Moe Capital LLC. We refer collectively to these categories of
        individuals as "you" throughout this policy.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Information We Collect</h2>
      <p className="mb-6">
        We collect various types of information to provide, improve, and secure our services. The categories of
        information we collect include:
        <ul className="list-inside list-disc">
          <li>
            <strong>Personal Information:</strong> This includes your username, email address, and payment information,
            which is processed through third-party payment processing vendors.
          </li>
          <li>
            <strong>User Content:</strong> This includes any content you generate or upload while using our services,
            such as chat logs, messages, and other user-generated content.
          </li>
          <li>
            <strong>Communications:</strong> This includes any communications you have with us, whether through email,
            chat, or other forms of communication.
          </li>
          <li>
            <strong>Browser Metadata:</strong> This includes information automatically collected by your browser, such
            as cookies, page views, referral URLs, IP addresses, access times, ISP information, operating system
            details, browser type, and other similar data.
          </li>
          <li>
            <strong>Tracking Data:</strong> This includes data collected through tracking technologies like 1x1 pixels,
            clear GIFs, and similar mechanisms.
          </li>
          <li>
            <strong>Third-Party Sources:</strong> This includes information we obtain from third-party sources, such as
            social media platforms, data brokers, and other external services.
          </li>
        </ul>
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">How We Use Your Information</h2>
      <p className="mb-6">
        We use the information we collect for various purposes, including:
        <ul className="list-inside list-disc">
          <li>
            <strong>Training Machine Learning Models:</strong> We use user content and interactions to train and improve
            our machine learning models, which enhance the performance and accuracy of our services.
          </li>
          <li>
            <strong>Communication:</strong> We use your contact information to communicate with you about our services,
            updates, and other relevant information.
          </li>
          <li>
            <strong>Regulatory Compliance:</strong> We use your information to comply with legal obligations, including
            tax, financial reporting, and other regulatory requirements.
          </li>
          <li>
            <strong>Service Personalization:</strong> We use your information to personalize your experience with our
            services, including tailoring content and recommendations based on your preferences and behavior.
          </li>
          <li>
            <strong>Service Development:</strong> We use your information to develop new services, features, and
            functionalities that meet the needs of our users.
          </li>
          <li>
            <strong>De-Identified and Aggregated Information:</strong> We may use or disclose de-identified or
            aggregated information for any purpose, including research, analytics, and marketing, without restriction.
          </li>
        </ul>
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">When We Disclose Your Information</h2>
      <p className="mb-6">
        We disclose your information in the following circumstances:
        <ul className="list-inside list-disc">
          <li>
            <strong>Affiliates:</strong> We may share your information with our affiliates and subsidiaries for the
            purposes outlined in this policy.
          </li>
          <li>
            <strong>Vendors and Service Providers:</strong> We may share your information with third-party vendors and
            service providers who assist us in operating our business, such as payment processors, cloud storage
            providers, and customer support services.
          </li>
          <li>
            <strong>Advertising:</strong> We may share your information with advertising partners to deliver targeted
            advertisements and measure advertising effectiveness.
          </li>
          <li>
            <strong>User-Created Content:</strong> Your user-generated content may be visible to other users of our
            services, depending on the settings and features you use.
          </li>
          <li>
            <strong>Regulatory Compliance:</strong> We may disclose your information to comply with applicable laws,
            regulations, legal processes, or governmental requests.
          </li>
          <li>
            <strong>Business Transfers:</strong> In the event of a merger, acquisition, reorganization, sale of assets,
            or other business transaction, we may transfer your information to the acquiring entity.
          </li>
          <li>
            <strong>With Your Consent:</strong> We may disclose your information to third parties with your explicit
            consent.
          </li>
        </ul>
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">How We Secure Your Data</h2>
      <p className="mb-6">
        We take extensive measures to secure your data:
        <ul className="list-inside list-disc">
          <li>
            <strong>Encryption in Transit:</strong> All data is encrypted using SSL/TLS protocols during transmission to
            ensure secure communication.
          </li>
          <li>
            <strong>Encryption at Rest:</strong> Your data is encrypted at rest using AES-256, a robust encryption
            standard that protects your information from unauthorized access.
          </li>
          <li>
            <strong>Database Backups:</strong> Our database backups are also encrypted to safeguard your data in the
            event of a system failure or other data loss scenario.
          </li>
          <li>
            <strong>Access Controls:</strong> We implement strict access controls to ensure that only authorized
            personnel can access your data.
          </li>
          <li>
            <strong>Regular Audits:</strong> We conduct regular security audits and vulnerability assessments to
            identify and mitigate potential security risks.
          </li>
        </ul>
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Data Retention</h2>
      <p className="mb-6">
        We retain your information for as long as necessary to fulfill the purposes outlined in this policy, unless a
        longer retention period is required or permitted by law. The retention period varies depending on the type of
        information and the purpose for which it was collected. After the retention period expires, we may delete or
        aggregate your information. We may also retain and use your information as necessary to comply with our legal
        obligations, resolve disputes, and enforce our agreements.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Data Transfers</h2>
      <p className="mb-6">
        Our products and services are operated in the United States. If you are located in the European Union, the UK,
        or elsewhere outside of the United States, please be aware that any information you provide to us will be
        transferred to and stored in the United States. By using our websites or services and/or providing us with your
        personal information, you consent to this transfer. We take steps to ensure that your information is protected
        in accordance with applicable data protection laws, including the use of standard contractual clauses and other
        appropriate safeguards.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Changes to This Policy</h2>
      <p className="mb-6">
        We may update this Privacy Policy as needed to comply with relevant regulations and reflect any new practices.
        Whenever we make significant changes to our policies, we will update the date at the top of this page and take
        appropriate steps to notify users, such as through email notifications or prominent notices on our websites.
      </p>

      <h2 className="text-tx-secondary mb-4 text-2xl font-semibold">Contact Us</h2>
      <p className="mb-6">
        If you have any questions about our Terms of Service, Privacy Policy, or Legal Notices, please contact us at{" "}
        <a href="mailto:hey@anime.gf" className="text-blue-500 hover:underline">
          hey@anime.gf
        </a>
        .
      </p>
    </div>
  );
}
